"use client";

import { useEffect, useState } from "react";

import type { BookCallPopupVariant } from "@/lib/book-call-popup-copy";
import { useBookCallTriggerProps } from "@/lib/use-book-call-trigger-props";

type CcShopifyBadgeProps = {
  copy: { label: string; aria: string };
  /** Booking popup to open; the landing page sub-page reuses the badge. */
  bookCallVariant?: BookCallPopupVariant;
  /** The badge shows once this element has scrolled away. */
  heroId?: string;
};

/**
 * Round "book a call" badge with rotating text, pinned bottom-left
 * (charleagency.com's "Get in touch"). Appears once the hero has scrolled
 * away so it never competes with the hero CTA, and opens the booking popup.
 */
export function CcShopifyBadge({
  copy,
  bookCallVariant = "shopify",
  heroId = "shopify-hero",
}: CcShopifyBadgeProps) {
  const triggerProps = useBookCallTriggerProps({ bookCallVariant });
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const hero = document.getElementById(heroId);
    if (!hero) return;
    const observer = new IntersectionObserver(([entry]) => setVisible(!entry.isIntersecting), {
      rootMargin: "-40% 0px 0px 0px",
    });
    observer.observe(hero);
    return () => observer.disconnect();
  }, [heroId]);

  return (
    <button
      type="button"
      className="cc-shop-badge"
      data-visible={visible ? "true" : "false"}
      aria-label={copy.aria}
      tabIndex={visible ? 0 : -1}
      {...triggerProps}
    >
      <svg className="cc-shop-badge__ring" viewBox="0 0 100 100" aria-hidden="true">
        <defs>
          <path id="cc-shop-badge-circle" d="M50 50 m-38 0 a38 38 0 1 1 76 0 a38 38 0 1 1 -76 0" />
        </defs>
        <text>
          <textPath href="#cc-shop-badge-circle">{copy.label.repeat(2)}</textPath>
        </text>
      </svg>
      <span className="cc-shop-badge__core" aria-hidden="true">
        <svg viewBox="0 0 24 24">
          <path d="M7 17L17 7M9 7h8v8" />
        </svg>
      </span>
    </button>
  );
}
